<script setup lang="ts">
import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { taskCategoriesApi, type TaskCategory, type TaskCategoryForm } from '@/api/task-categories';

interface Props {
  taskCategory?: TaskCategory | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const queryClient = useQueryClient();

const isEditing = !!props.taskCategory;
const title = computed(() => (isEditing ? 'Editar Categoría' : 'Nueva Categoría'));

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 100;
const MIN_DESCRIPTION_LENGTH = 10;
const MAX_DESCRIPTION_LENGTH = 500;

const validationSchema = yup.object({
  name: yup
    .string()
    .required('El nombre es requerido')
    .min(MIN_NAME_LENGTH, `El nombre debe tener al menos ${MIN_NAME_LENGTH} caracteres`)
    .max(MAX_NAME_LENGTH, `El nombre no puede exceder ${MAX_NAME_LENGTH} caracteres`),
  description: yup
    .string()
    .test(
      'min-when-present',
      `La descripción debe tener al menos ${MIN_DESCRIPTION_LENGTH} caracteres`,
      (value) => !value || value.length >= MIN_DESCRIPTION_LENGTH,
    )
    .max(MAX_DESCRIPTION_LENGTH, `La descripción no puede exceder ${MAX_DESCRIPTION_LENGTH} caracteres`),
});

const { values, errors, defineField, handleSubmit, meta } = useForm<TaskCategoryForm>({
  validationSchema,
  initialValues: {
    name: props.taskCategory?.name || '',
    description: props.taskCategory?.description || '',
  },
});

const [name, nameAttrs] = defineField('name');
const [description, descriptionAttrs] = defineField('description');

const createMutation = useMutation({
  mutationFn: () => taskCategoriesApi.create(values),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['taskCategories'] });
    emit('success');
    emit('close');
  },
});

const updateMutation = useMutation({
  mutationFn: () => taskCategoriesApi.update({ ...values, id: props.taskCategory?.id }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['taskCategories'] });
    emit('success');
    emit('close');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

const onSubmit = handleSubmit(() => {
  if (isEditing && props.taskCategory) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}
</script>
<template>
  <v-card>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-text-field
          v-model="name"
          label="Nombre de la categoría"
          :error-messages="errors.name"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="nameAttrs"
        />

        <v-textarea
          v-model="description"
          label="Descripción (opcional)"
          :error-messages="errors.description"
          :disabled="isLoading"
          variant="outlined"
          rows="4"
          counter="500"
          v-bind="descriptionAttrs"
        />
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ isEditing ? 'Actualizar' : 'Crear' }}
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="createMutation.isError.value || updateMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente. Recuerda que el nombre no puede repetirse.
    </v-alert>
  </v-card>
</template>
